import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-hidden focus-visible:border-ring focus-visible:ring-ring/60 focus-visible:ring-[3px] focus-visible:ring-offset-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/95",
        // `primary` is the PAGE-PRIMARY emphasis tier (#8097), NOT a synonym for
        // `default`. Until 2026-07-17 the two were byte-identical, so the ~41
        // call-sites that reached for `variant="primary"` to mark the one action
        // a page exists for rendered indistinguishably from every other button —
        // the "everything is equal weight" complaint, at the primitive layer.
        // It now carries added weight (semibold + elevation) so exactly one
        // control per page can out-rank its siblings.
        // `default` is deliberately UNCHANGED: it is the implicit variant on
        // every unspecified <Button>, so re-weighting it would re-weight the
        // whole app. Emphasis is opt-in.
        primary:
          "bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary/90 hover:shadow-md active:bg-primary/95 active:shadow-sm",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 active:bg-destructive/95 focus-visible:ring-destructive/30 dark:focus-visible:ring-destructive/50 dark:bg-destructive/60",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground active:bg-accent/80 dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/90",
        ghost: "hover:bg-accent hover:text-accent-foreground active:bg-accent/80 dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-9 sm:h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
      },
      elevation: {
        none: "",
        default: "shadow-sm hover:shadow-md active:shadow-sm transition-shadow",
        raised:
          "shadow-md hover:shadow-lg active:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      elevation: "none",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, elevation, asChild = false, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot
          data-slot="button"
          className={cn(buttonVariants({ variant, size, elevation, className }))}
          ref={ref as React.Ref<HTMLElement>}
          {...props}
        />
      );
    }

    return (
      <button
        data-slot="button"
        className={cn(buttonVariants({ variant, size, elevation, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);

Button.displayName = "Button";

export { Button, buttonVariants };
